package fr.frux.feature.site.detail

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.site.resources.Res
import fr.frux.feature.site.resources.site_owner_claim
import fr.frux.feature.site.resources.site_owner_claim_text
import fr.frux.feature.site.resources.site_owner_rule_ask
import fr.frux.feature.site.resources.site_owner_rule_hint
import fr.frux.feature.site.resources.site_owner_rule_open
import fr.frux.feature.site.resources.site_owner_rule_private
import fr.frux.feature.site.resources.site_owner_rule_title
import fr.frux.feature.site.resources.site_owner_unclaim
import fr.frux.feature.site.resources.site_owner_unclaim_hint
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.SiteAccessRule
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

private val RULES: List<Pair<SiteAccessRule, StringResource>> =
    listOf(
        SiteAccessRule.OPEN to Res.string.site_owner_rule_open,
        SiteAccessRule.ASK to Res.string.site_owner_rule_ask,
        SiteAccessRule.PRIVATE to Res.string.site_owner_rule_private,
    )

/**
 * Ce que la fiche offre à qui peut se déclarer propriétaire, puis au propriétaire (maquette
 * FicheProprietaire). Rien pour les autres.
 */
@Composable
internal fun SiteOwnerSection(
    site: Site,
    ui: SiteOwnershipUi,
    onClaim: () -> Unit,
    onRuleSelected: (SiteAccessRule) -> Unit,
    onUnclaim: () -> Unit,
    modifier: Modifier = Modifier,
) {
    when {
        site.isOwner -> OwnerControls(site, ui, onRuleSelected, onUnclaim, modifier)
        site.canClaim -> ClaimCard(ui, onClaim, modifier)
    }
}

@Composable
private fun ClaimCard(
    ui: SiteOwnershipUi,
    onClaim: () -> Unit,
    modifier: Modifier,
) {
    Surface(
        shape = MaterialTheme.shapes.medium,
        color = MaterialTheme.colorScheme.tertiaryContainer,
        contentColor = MaterialTheme.colorScheme.onTertiaryContainer,
        modifier = modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier.padding(FruxSpacing.md),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Text(text = stringResource(Res.string.site_owner_claim_text), style = MaterialTheme.typography.bodyMedium)
            FruxButton(
                text = stringResource(Res.string.site_owner_claim),
                onClick = onClaim,
                enabled = !ui.busy,
            )
        }
    }
}

@Composable
private fun OwnerControls(
    site: Site,
    ui: SiteOwnershipUi,
    onRuleSelected: (SiteAccessRule) -> Unit,
    onUnclaim: () -> Unit,
    modifier: Modifier,
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        Text(
            text = stringResource(Res.string.site_owner_rule_title),
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.semantics { heading() },
        )
        Text(
            text = stringResource(Res.string.site_owner_rule_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        SingleChoiceSegmentedButtonRow(modifier = Modifier.fillMaxWidth()) {
            RULES.forEachIndexed { index, (rule, label) ->
                SegmentedButton(
                    selected = site.accessRule == rule,
                    onClick = { if (site.accessRule != rule) onRuleSelected(rule) },
                    shape = SegmentedButtonDefaults.itemShape(index, RULES.size),
                    enabled = !ui.busy,
                ) {
                    Text(stringResource(label))
                }
            }
        }
        FruxButton(
            text = stringResource(Res.string.site_owner_unclaim),
            onClick = onUnclaim,
            enabled = !ui.busy,
            style = FruxButtonStyle.Text,
        )
        Text(
            text = stringResource(Res.string.site_owner_unclaim_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}
